import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { fmt$Denominated } from "../../utils/format";
import { useRole } from "../../hooks/useRole";

/**
 * The issuer onboarding fee, on the screen where a campaign is approved.
 *
 * WHY IT EXISTS. Until now the admin console had NO fee surface of any kind — a
 * grep for fee_obligation across this whole repo returned nothing. The ledger
 * row, its reference code, its due date and its wire coordinates existed only in
 * the database, and there was no way to record that the fee had been RECEIVED:
 * campaign_fee_obligations.status has carried 'paid', 'waived' and 'cancelled'
 * since the table was created and nothing anywhere wrote any of them. Marking a
 * payment meant a hand-written SQL UPDATE.
 *
 * Schedule "A" cl.1 makes this the point of the fee — it is "due upon
 * submission", and RFI "has no obligation to commence onboarding, review,
 * listing or due diligence until it is received in full". A right to withhold is
 * unexercisable while receipt is unrecordable.
 *
 * IT GATES NOTHING. Whether approval should be blocked on a paid fee is a
 * product decision — cl.1 grants a right to withhold, it does not impose a duty
 * — and wiring it here would take that decision by accident. This panel reports
 * the fact and lets an admin record it.
 *
 * paid_at IS THE VALUE DATE ON THE BANK STATEMENT, asked for explicitly rather
 * than defaulted to today. A default would look right and quietly record the
 * wrong date on every row, which is unrecoverable once the statement is filed.
 */

const API = process.env.REACT_APP_API;
const headers = () => ({ "x-access-token": localStorage.getItem("accessToken") });

const STATUS_COPY = {
  pending_config: {
    label: "Not yet payable",
    hint:
      "The fee amount or the wire coordinates are not configured for this currency, " +
      "so the issuer cannot pay it. Check the issuer_onboarding_fee and " +
      "issuer_fee_wire_instructions config before chasing the issuer.",
  },
  pending_payment: { label: "Awaiting payment", hint: "" },
  paid: { label: "Paid", hint: "" },
  waived: { label: "Waived", hint: "" },
  cancelled: { label: "Cancelled", hint: "" },
};

const SETTLED = ["paid", "waived", "cancelled"];

// C4: why a fee needs a person, in words. Stored apart from `status` (the
// nullable attention_reason), so code that predates it reads the row unchanged.
const ATTENTION_COPY = {
  issuer_country_missing:
    "No fee was issued: the issuer's company country is not set, so the fee currency " +
    "cannot be chosen. Ask the issuer to add the company country.",
  rebill_required:
    "The issuer's country was corrected after this fee was issued, so it is billed in the " +
    "wrong currency. Re-issue it to cancel this fee and issue a new one in the right " +
    "currency, with a new payment reference.",
};

/** The API refuses a re-issue reason shorter than this (after trimming). */
const MIN_REISSUE_REASON_LENGTH = 20;

const Row = ({ label, children }) => (
  <div className="detailItem">
    <span className="itemKey">{label}</span>
    <span className="itemValue">{children}</span>
  </div>
);

// Shown verbatim off the stored string. Passing a date-only value through a Date
// renders the previous day west of UTC.
const day = (v) => (v ? String(v).slice(0, 10) : "— not set");

// D1: due_date is an INSTANT (23:59 on the deadline day in Toronto, sent as UTC),
// so its UTC slice is the next day: 2026-11-03T04:59:59Z is 2 Nov, as the e-mail
// says. Shown in Toronto, named, whatever the browser's zone. A date-only string
// still goes through day(), never through a Date.
const TORONTO = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Toronto",
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});
const deadline = (v) => {
  if (!v || /^\d{4}-\d{2}-\d{2}$/.test(String(v))) return day(v);
  const at = new Date(v);
  if (Number.isNaN(at.getTime())) return day(v);
  const p = Object.fromEntries(TORONTO.formatToParts(at).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute} Toronto`;
};

export default function FeeObligationPanel({ campaignId, canSettle = true }) {
  const [state, setState] = useState({ loading: true, error: null, fee: null });
  // Re-issue is AD only on the server (requireRole('AD')); hasRole mirrors it,
  // legacy Super Admin wildcard included.
  const { hasRole } = useRole();

  const load = async () => {
    if (!campaignId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(
        `${API}admin/campaign-fee-obligation/${campaignId}`,
        { headers: headers() }
      );
      setState({ loading: false, error: null, fee: res?.data?.data || null });
    } catch (e) {
      // A 404 is not an error worth shouting about: plenty of campaigns predate
      // fee obligations entirely, and every one of them would otherwise show a
      // red banner on a screen an admin uses constantly.
      const missing = e?.response?.status === 404;
      setState({
        loading: false,
        error: missing ? null : e?.response?.data?.message || "Could not load the fee obligation.",
        fee: null,
      });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const settle = async (status) => {
    const isPayment = status === "paid";
    const { value, isConfirmed } = await Swal.fire({
      title: isPayment ? "Record payment received" : `Mark this fee ${status}`,
      html:
        (isPayment
          ? '<p style="text-align:left;margin:0 0 8px">Enter the VALUE DATE of the credit on the bank statement, not today’s date.</p>' +
            '<input id="fee-paid-at" type="date" class="swal2-input" style="width:auto">'
          : "") +
        (isPayment
          ? '<p style="text-align:left;margin:12px 0 4px">Amount CREDITED, as it appears on the statement.</p>'
            + `<input id="fee-amount" class="swal2-input" style="width:auto" placeholder="${fee.amount ?? ""}">`
          : "") +
        '<input id="fee-ref" class="swal2-input" placeholder="Bank reference (optional)">' +
        '<input id="fee-note" class="swal2-input" placeholder="Note (optional)">',
      showCancelButton: true,
      confirmButtonText: isPayment ? "Record payment" : `Mark ${status}`,
      focusConfirm: false,
      preConfirm: () => {
        const paidAt = isPayment ? document.getElementById("fee-paid-at")?.value : null;
        if (isPayment && !paidAt) {
          Swal.showValidationMessage("The value date is required to record a payment.");
          return false;
        }
        // WHAT THE BANK CREDITED, sent for verification. The API compares it to
        // the obligation and refuses a mismatch with 409 amount_mismatch /
        // currency_mismatch. Until this field existed that check was INERT: the
        // service accepted expectedAmount but nothing ever supplied it, so a
        // partial credit and a payment in full closed the receivable identically.
        //
        // The placeholder shows the amount owed rather than pre-filling it. A
        // pre-filled field is a confirmation click, and a verification the admin
        // confirms without reading verifies nothing.
        const amount = isPayment ? document.getElementById("fee-amount")?.value : null;
        if (isPayment && !String(amount || "").trim()) {
          Swal.showValidationMessage("Enter the amount credited, so it can be checked against the fee.");
          return false;
        }
        return {
          paid_at: paidAt || undefined,
          expected_amount: amount || undefined,
          // The obligation's own currency, so a USD statement line recorded
          // against a CAD receivable is refused rather than silently accepted.
          expected_currency: isPayment ? fee.currency || undefined : undefined,
          reference: document.getElementById("fee-ref")?.value || undefined,
          note: document.getElementById("fee-note")?.value || undefined,
        };
      },
    });
    if (!isConfirmed || !value) return;

    try {
      await axios.post(
        `${API}admin/campaign-fee-obligation/${campaignId}/settle`,
        // The bill this panel shows. A re-issue keeps the row and gives it a new
        // reference, so the API refuses a settlement naming the old one
        // (409 obligation_changed) instead of recording it against a bill the
        // admin never saw. "" for a fee with no reference yet.
        { status, ...value, expected_reference: fee.reference_code ?? "" },
        { headers: headers() }
      );
      await load();
      Swal.fire({ icon: "success", title: "Recorded", timer: 1600, showConfirmButton: false });
    } catch (e) {
      // 409 covers three refusals now: already_settled (the compare-and-swap
      // losing a race), amount_mismatch and currency_mismatch (the verification
      // above). Reload on all of them so the admin sees the server's version of
      // the row rather than a bare error - for a mismatch that is the figure
      // they were supposed to be checking against.
      if (e?.response?.status === 409) await load();
      Swal.fire({
        icon: "error",
        title: e?.response?.data?.message || "Could not record the settlement.",
      });
    }
  };

  // C4: cancels this unpaid fee with the reason recorded, then issues a fresh
  // one (new reference, the current currency rule). The API audits both and
  // refuses a paid or waived fee.
  const reissue = async () => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: "Re-issue this fee",
      text:
        "This fee is cancelled with your reason, and a new one is issued in the currency the " +
        "fee rule now gives, with a new payment reference. Both are recorded in the audit log.",
      input: "textarea",
      inputPlaceholder: `Why is this fee being re-issued? (at least ${MIN_REISSUE_REASON_LENGTH} characters)`,
      showCancelButton: true,
      confirmButtonText: "Re-issue fee",
      inputValidator: (v) =>
        String(v || "").trim().length >= MIN_REISSUE_REASON_LENGTH
          ? undefined
          : `Give a reason of at least ${MIN_REISSUE_REASON_LENGTH} characters; it is recorded in the audit log.`,
    });
    if (!isConfirmed || !reason) return;

    try {
      await axios.post(
        `${API}admin/campaign-fee-obligation/${campaignId}/reissue`,
        // As settle: the bill this panel shows, so a fee another admin
        // re-issued meanwhile is refused (409 obligation_changed) rather than
        // cancelled again under the issuer who is paying it.
        { reason: String(reason).trim(), expected_reference: fee.reference_code ?? "" },
        { headers: headers() }
      );
      await load();
      Swal.fire({ icon: "success", title: "Fee re-issued", timer: 1600, showConfirmButton: false });
    } catch (e) {
      // As settle: on a 409 the row changed (paid, waived, already re-issued),
      // so show the server's version of it.
      if (e?.response?.status === 409) await load();
      Swal.fire({
        icon: "error",
        title: e?.response?.data?.message || "Could not re-issue the fee.",
      });
    }
  };

  if (state.loading) return null;
  if (state.error) {
    return (
      <div className="detailItem">
        <span className="itemKey">Onboarding Fee</span>
        <span className="itemValue">{state.error}</span>
      </div>
    );
  }
  // No obligation: say so plainly. A campaign submitted after the fee shipped and
  // carrying no row is exactly what the reconciliation script hunts for, so this
  // absence is information rather than an empty state to hide.
  if (!state.fee) {
    return (
      <div className="detailItem">
        <span className="itemKey">Onboarding Fee</span>
        <span className="itemValue">No fee obligation exists for this campaign.</span>
      </div>
    );
  }

  const fee = state.fee;
  const wire = fee.wire || {};
  const copy = STATUS_COPY[fee.status] || { label: fee.status, hint: "" };
  const settled = SETTLED.includes(fee.status);
  const attention = fee.attention_reason || null;
  // With no issuer country, that is the cause of pending_config; the config
  // hint would send the admin after the wrong one.
  const hint = attention === "issuer_country_missing" ? "" : copy.hint;

  return (
    <>
      <Row label="Onboarding Fee">
        {fmt$Denominated(fee.amount, 2, fee.currency)}
      </Row>
      <Row label="Fee Status">
        {copy.label}
        {hint ? <div className="itemHint">{hint}</div> : null}
      </Row>
      {attention ? (
        <Row label="Needs Attention">
          {ATTENTION_COPY[attention] || attention}
          {attention === "rebill_required" && !settled && hasRole("AD") ? (
            <div>
              <button type="button" className="btn btn-sm btn-primary" onClick={reissue}>
                Re-issue fee
              </button>
            </div>
          ) : null}
        </Row>
      ) : null}
      <Row label="Payment Reference">{fee.reference_code || "— not set"}</Row>
      <Row label="Fee Due">{deadline(fee.due_date)}</Row>
      {fee.paid_at ? <Row label="Received">{day(fee.paid_at)}</Row> : null}
      {fee.paid_reference ? <Row label="Bank Reference">{fee.paid_reference}</Row> : null}

      {/* The coordinates the issuer was actually given. Shown so an admin
          chasing a payment quotes the same account the issuer was told to pay
          into — CAD and USD are different accounts, and USD carries an
          intermediary leg that CAD does not. */}
      {wire.beneficiary_account ? (
        <Row label="Fee Wire">
          {[wire.bank_name, wire.beneficiary_account, wire.swift_code]
            .filter(Boolean)
            .join(" · ")}
          {wire.correspondent_bank ? (
            <div className="itemHint">
              via {wire.correspondent_bank}
              {wire.correspondent_swift ? ` (${wire.correspondent_swift})` : ""}
            </div>
          ) : null}
        </Row>
      ) : null}

      {canSettle && !settled ? (
        <div className="detailItem">
          <span className="itemKey">Record</span>
          <span className="itemValue">
            <button type="button" className="btn btn-sm btn-primary" onClick={() => settle("paid")}>
              Payment received
            </button>{" "}
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => settle("waived")}>
              Waive
            </button>{" "}
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => settle("cancelled")}>
              Cancel
            </button>
          </span>
        </div>
      ) : null}
    </>
  );
}
